import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import SkeletonFacebookAdCardListItem from '../../../../../CampaignsSummary/AdsList/FacebookAdsPreviewList/FacebookAdCardListItem/Skeleton'
import FacebookAdCardListItem from '../../../../../CampaignsSummary/AdsList/FacebookAdsPreviewList/FacebookAdCardListItem'
import ItemsLoading from '../../../../../../components/Loaders/ItemsLoading'
import AdsWrapper from '../AdsWrapper'

import { getVideoIds } from '../../../../../../forms/Facebook/AdForms/AdFacebookEdit/helpers'
import { useFacebookMediaLoading } from '../../../../../CampaignsSummary/AdsList/FacebookAdsPreviewList/useFacebookMediaLoading'
import { useSortAds } from '../hooks'
import { getAllImageHashesFromAdsList } from '../../../../../CampaignsSummary/AdsList/FacebookAdsPreviewList/helpers'
import { useDeleteAdAction } from '../../../../../../features/hooks/useDeleteAction/useDeleteAdAction'

import {
  combinedAdsErrorSelector,
  combinedAdsIsLoadingSelector,
  combinedAdsSelector,
  combinedAdsWasLoadedSelector,
  combinedLineItemsIsLoadingSelector,
  combinedLineItemsSelector
} from '../../../../../../modules/selectors/combinedData'
import { selectedAdAccountsSelector } from '../../../../../../modules/selectors/adAccounts'
import {
  accessibleSocialPagesErrorSelector,
  accessibleSocialPagesWasLoadedSelector
} from '../../../../../../modules/selectors/socialAccounts'
import { clearAccessibleSocialPages, getAccessibleSocialPages } from '../../../../../../modules/actions/socialAccounts'
import { clearChoicesCTA, getChoicesCTA } from '../../../../../../modules/actions/choices'
import { choicesCTAWasLoadedSelector } from '../../../../../../modules/selectors/choices'

import { FACEBOOK_PLATFORM, FACEBOOK_PROVIDER_PLATFORM } from '../../../../../../constants/selectLists/platformList'
import { PLACEMENT_POSITIONS } from '../../../../../../forms/Multiplatform/LineItemForms/fields'
import { DELETE_AD_OPTION } from '../../../../../CampaignsSummary/AdsList/FacebookAdsPreviewList/FacebookAdCardListItem/useFacebookAdActions'

import useFacebookAdsListStyles from '../../../../../CampaignsSummary/AdsList/FacebookAdsPreviewList/styles'

// For now on combined facebook ads we support only delete action
const supportedActions = { [DELETE_AD_OPTION]: true }

const FacebookHighestPerformingAds = () => {
  const facebookAdsListClasses = useFacebookAdsListStyles()
  const dispatch = useDispatch()

  const { [FACEBOOK_PLATFORM]: selectedAdAccount = {} } = useSelector(selectedAdAccountsSelector)
  const { currency_symbol: currencySymbol } = selectedAdAccount

  const combinedLineItemsIsLoading = useSelector(combinedLineItemsIsLoadingSelector)
  const combinedAds = useSelector(combinedAdsSelector)
  const facebookPagesWasLoaded = useSelector(accessibleSocialPagesWasLoadedSelector)
  const facebookPagesError = useSelector(accessibleSocialPagesErrorSelector)
  const choicesCTAWasLoaded = useSelector(choicesCTAWasLoadedSelector)

  // allow pages to be loaded with error
  const accessibleSocialPagesDidLoading = facebookPagesWasLoaded || facebookPagesError

  const combinedLineItems = useSelector(combinedLineItemsSelector)

  const facebookAds = useMemo(() => {
    return combinedAds.filter(ad => ad.provider === FACEBOOK_PROVIDER_PLATFORM)
  }, [combinedAds])

  const sortedFacebookAds = useSortAds(facebookAds)

  const imageHashes = useMemo(() => {
    return getAllImageHashesFromAdsList(facebookAds)
  }, [facebookAds])

  const videoIds = useMemo(() => {
    return facebookAds?.length ? facebookAds.map(ad => getVideoIds(ad)).flat() : []
  }, [facebookAds])

  // todo use case 2
  const { initialMediaIsLoading } = useFacebookMediaLoading({ imageHashes, videoIds })

  const skeletonProps = useMemo(
    () => ({
      showStatus: false,
      showDetails: false,
      showMetrics: true,
      showActionsButtons: false,
      isAdApprovalsStyle: false
    }),
    []
  )

  const { handleDeleteRequest, DeleteAdModal } = useDeleteAdAction({ deleteCombinedAd: true })

  const renderedAdsListCards = useMemo(
    () =>
      sortedFacebookAds?.map(ad => {
        const { id } = ad

        const allLineItems = combinedLineItems.flatMap(({ line_items }) => line_items)
        const parentLineItem = allLineItems.find(({ id }) => id === ad.line_item_id)
        const placementPositions = parentLineItem?.[PLACEMENT_POSITIONS] || []

        return (
          <FacebookAdCardListItem
            key={id}
            ad={ad}
            currencySymbol={currencySymbol}
            showStatus={false}
            showDetails={true}
            showMetrics={true}
            showActionsDropdown={true}
            supportedActions={supportedActions}
            showActionsButtons={false}
            providedPlacementPositions={placementPositions}
            handleDeleteRequest={handleDeleteRequest}
          />
        )
      }),
    [sortedFacebookAds, currencySymbol, combinedLineItems, handleDeleteRequest]
  )

  const additionalDataIsLoading = useMemo(
    () =>
      initialMediaIsLoading || combinedLineItemsIsLoading || !accessibleSocialPagesDidLoading || !choicesCTAWasLoaded,
    [initialMediaIsLoading, combinedLineItemsIsLoading, accessibleSocialPagesDidLoading, choicesCTAWasLoaded]
  )

  useEffect(() => {
    dispatch(getChoicesCTA())

    return () => {
      dispatch(clearChoicesCTA())
    }
  }, [dispatch])

  useEffect(() => {
    dispatch(
      getAccessibleSocialPages({
        platform: FACEBOOK_PROVIDER_PLATFORM
      })
    )

    return () => {
      dispatch(clearAccessibleSocialPages())
    }
  }, [dispatch])

  return (
    <>
      <section id="facebook-ad-cards-list">
        <ItemsLoading
          itemsLength={sortedFacebookAds.length}
          className={facebookAdsListClasses.cardsList}
          noDataText={'There are no highest performing Facebook ads'}
          isLoadingSelector={combinedAdsIsLoadingSelector}
          wasLoadedSelector={combinedAdsWasLoadedSelector}
          errorSelector={combinedAdsErrorSelector}
          additionalDataIsLoading={additionalDataIsLoading}
          initialLoadingSkeletonsNumber={5}
          SkeletonComponent={SkeletonFacebookAdCardListItem}
          ContentWrapper={AdsWrapper}
          skeletonProps={skeletonProps}
        >
          {renderedAdsListCards}
        </ItemsLoading>
      </section>
      {DeleteAdModal}
    </>
  )
}

export default FacebookHighestPerformingAds
